import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangleIcon, RefreshCwIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

type Props = {
  children: ReactNode;
};

type State = {
  error: Error | null;
  isStaleBuild: boolean;
};

// Detects the "user has old bundle loaded, a lazy-imported chunk 404s against the new deploy" case.
// Covers Chrome, Firefox, Safari wording plus the synthetic ChunkLoadError that some bundlers throw.
function isStaleBuildError(error: Error): boolean {
  const name = error.name || "";
  const message = error.message || "";

  if (name === "ChunkLoadError") {
    return true;
  }

  return (
    /Loading chunk \S+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Unable to preload CSS/i.test(message)
  );
}

// Force a cache-bypassing reload. WordPress admin pages aren't cached, so reloading the
// top-level document reliably pulls fresh HTML which references the new hashed chunk filenames.
// We still append a cache-bust param as a belt-and-braces defense against any proxy/CDN in front of WP.
function forceReload(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_cb", Date.now().toString(36));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, isStaleBuild: false };
  private reloadTimer: number | null = null;

  static getDerivedStateFromError(error: Error): State {
    return { error, isStaleBuild: isStaleBuildError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[markaz] Unhandled render error:", error, info.componentStack);
  }

  componentDidUpdate(_prevProps: Props, prevState: State): void {
    // If we just entered a stale-build error state, auto-reload after a short delay so the
    // user sees why the page blipped before the refresh happens.
    if (this.state.isStaleBuild && !prevState.isStaleBuild && this.reloadTimer === null) {
      this.reloadTimer = window.setTimeout(forceReload, 1500);
    }
  }

  componentWillUnmount(): void {
    if (this.reloadTimer !== null) {
      window.clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }
  }

  private handleReload = (): void => {
    forceReload();
  };

  render(): ReactNode {
    const { error, isStaleBuild } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <div
        dir="rtl"
        className="min-h-screen w-full flex items-center justify-center bg-background p-6"
      >
        <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div
              className={
                "flex size-10 items-center justify-center rounded-full " +
                (isStaleBuild
                  ? "bg-primary/10 text-primary"
                  : "bg-destructive/10 text-destructive")
              }
            >
              <RefreshCwIcon
                className={"size-5" + (isStaleBuild ? " animate-spin" : " hidden")}
              />
              <AlertTriangleIcon
                className={"size-5" + (isStaleBuild ? " hidden" : "")}
              />
            </div>
            <h1 className="text-lg font-semibold text-foreground">
              {isStaleBuild ? "يتوفر إصدار جديد" : "حدث خطأ غير متوقع"}
            </h1>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            {isStaleBuild
              ? "تم إصدار تحديث جديد جارٍ إعادة تحميل الصفحة لتحميل أحدث نسخة..."
              : "تعذّر عرض هذه الصفحة بسبب خطأ. يمكنك إعادة تحميل الصفحة للمحاولة مرة أخرى."}
          </p>

          {import.meta.env.DEV && error.message ? (
            <pre
              className="mb-4 max-h-40 overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground"
              dir="ltr"
            >
              {error.message}
            </pre>
          ) : null}

          <Button onClick={this.handleReload} className="w-full gap-2">
            <RefreshCwIcon className="size-4" />
            {isStaleBuild ? "إعادة التحميل الآن" : "إعادة تحميل الصفحة"}
          </Button>
        </div>
      </div>
    );
  }
}
